/**
 * extractCitedMarkets — scan article prose for market mentions.
 *
 * Sonnet writes pre-match previews that naturally cite specific betting
 * markets ("over 2.5", "BTTS", "1X2", "Asian handicap"). Those mentions
 * are the punter-relevant markets for THIS match — not just the generic
 * top-3 popular markets. We surface them in priority order in the
 * article-page odds strip.
 *
 * Approach: keyword scan, not prompt-tagging. Existing articles in DB
 * have no engine-side tagging, so we can't depend on `[ODDS:…]` markers
 * the way prose-Stories does. A render-side scanner works on every
 * historical row without backfill.
 *
 * Returns market IDs in cms.markets_catalog format (camelCase), in the
 * ORDER they appeared in the prose. First mention wins for de-dup.
 *
 * Coverage is conservative — only well-known market families. False
 * positives are worse than misses (showing wrong odds is worse than
 * falling back to popular markets).
 */

/** All provider/Sportradar market families we recognise. The IDs match
 *  `cms.markets_catalog.id`. */
export type CitedMarketId =
  | 'match1x2'
  | 'over25'
  | 'over15'
  | 'over35'
  | 'btts'
  | 'overUnder' // generic over/under without specific line
  | 'asianHandicap'
  | 'drawNoBet'
  | 'halfTimeResult'
  | 'goalscorer'
  | 'corners_ou'
  | 'cards_ou';

interface KeywordRule {
  marketId: CitedMarketId;
  /** Case-insensitive substring patterns. ANY match in any case form. */
  patterns: readonly RegExp[];
}

const RULES: readonly KeywordRule[] = [
  // Match result (1X2) — explicit only. "match winner", "1X2", "result"
  // can match too many false positives, so we require unambiguous tokens.
  {
    marketId: 'match1x2',
    patterns: [
      /\b1\s*[xX]\s*2\b/,
      /\bmatch\s+winner\b/i,
      /\bmatch\s+result\b/i,
      /\bresultat(et)?\b/i, // Swedish/Danish: "resultatet"
    ],
  },
  // Over/Under variants — line-specific takes priority over generic
  {
    marketId: 'over25',
    patterns: [
      /\bover\s+2[.,]5\b/i,
      /\bunder\s+2[.,]5\b/i,
      /\bo\s*\/\s*u\s*2[.,]5\b/i,
    ],
  },
  {
    marketId: 'over15',
    patterns: [
      /\bover\s+1[.,]5\b/i,
      /\bunder\s+1[.,]5\b/i,
    ],
  },
  {
    marketId: 'over35',
    patterns: [
      /\bover\s+3[.,]5\b/i,
      /\bunder\s+3[.,]5\b/i,
    ],
  },
  // Generic over/under — only matches when no line-specific rule did.
  // The dedup logic below ensures specific lines win.
  {
    marketId: 'overUnder',
    patterns: [
      /\bgoals?\s+markets?\b/i,
      /\btotal\s+goals\b/i,
      /\bover\s*\/\s*under\b/i,
    ],
  },
  // BTTS
  {
    marketId: 'btts',
    patterns: [
      /\bBTTS\b/,
      /\bboth\s+teams?\s+to\s+score\b/i,
      /\bbegge\s+hold\s+scorer\b/i, // da-DK
      /\bbåda\s+lag(en)?\s+gör\s+mål\b/i, // sv-SE
    ],
  },
  // Asian handicap
  {
    marketId: 'asianHandicap',
    patterns: [
      /\bAsian\s+handicap\b/i,
      /\bAH\s*[+-]?\d/i,
      /\basiatisk\s+handicap\b/i,
    ],
  },
  // Draw no bet
  {
    marketId: 'drawNoBet',
    patterns: [
      /\bdraw\s+no\s+bet\b/i,
      /\bDNB\b/,
    ],
  },
  // Half-time result
  {
    marketId: 'halfTimeResult',
    patterns: [
      /\bhalf[\s-]?time\s+result\b/i,
      /\bHT\/FT\b/,
    ],
  },
  // Goalscorer markets
  {
    marketId: 'goalscorer',
    patterns: [
      /\b(top|first|any[\s-]?time)\s+goalscorer\b/i,
      /\bto\s+score\b/i,
      /\bmålskytt(arna)?\b/i, // sv-SE: målskytt(arna)
    ],
  },
  // Corners
  {
    marketId: 'corners_ou',
    patterns: [
      /\bcorners?\b/i,
      /\bhjørner?\b/i, // da-DK
      /\bhörn(or)?\b/i, // sv-SE
    ],
  },
  // Cards
  {
    marketId: 'cards_ou',
    patterns: [
      /\bbook(ing|ings)\b/i,
      /\byellow\s+cards?\b/i,
      /\bred\s+cards?\b/i,
      /\bkort\s+(o\/u|over)\b/i, // sv-SE/da-DK
    ],
  },
];

/**
 * Walk the prose ONCE and emit market IDs in first-mention order.
 * Each market ID appears at most once; the first match wins.
 *
 * Returns an empty array when the prose is empty or no market is cited.
 * The caller decides what to do then (e.g. fall back to top-3 popular).
 */
export function extractCitedMarkets(prose: string): CitedMarketId[] {
  if (!prose) return [];
  // Build a list of { marketId, firstIndex } by scanning every rule.
  // firstIndex is the lowest position in prose where the market is
  // mentioned. Then sort ascending and dedup, preserving first-mention
  // order across the whole prose.
  const hits: Array<{ marketId: CitedMarketId; idx: number }> = [];
  for (const rule of RULES) {
    let earliest = Number.POSITIVE_INFINITY;
    for (const pattern of rule.patterns) {
      const m = pattern.exec(prose);
      if (m && m.index < earliest) earliest = m.index;
    }
    if (earliest !== Number.POSITIVE_INFINITY) {
      hits.push({ marketId: rule.marketId, idx: earliest });
    }
  }
  hits.sort((a, b) => a.idx - b.idx);
  // De-dup AND apply specificity: if a line-specific over/under was
  // matched (over25/over15/over35), drop the generic `overUnder` mention.
  // Operators expect the specific market in the strip, not the family.
  const specificOU = hits.some(
    (h) => h.marketId === 'over25' || h.marketId === 'over15' || h.marketId === 'over35',
  );
  return hits
    .filter((h) => !(specificOU && h.marketId === 'overUnder'))
    .map((h) => h.marketId);
}

/**
 * Concatenate an article's sections into one prose blob for scanning.
 * Articles ship with `body: Section[]` where each section has its own
 * subheading + text. The scanner doesn't care about structure — only
 * total text content + global ordering.
 */
export function articleBodyAsProse(
  body: ReadonlyArray<{ subheading?: string; text?: string }>,
): string {
  return body
    .map((s) => [s.subheading ?? '', s.text ?? ''].join(' '))
    .join('\n\n');
}
